import React, { useCallback, type ReactNode } from 'react';
import SectionMessage from '@atlaskit/section-message';
import { Stack, Text } from '@atlaskit/primitives/compiled';
import Link from '@atlaskit/link';
import { Code, CodeBlock } from '@atlaskit/code';
import { useIntl } from '@atlassian/jira-intl';
import type { ApplicationLinkV3Status } from '../../../../common/entities.tsx';
import { lookupTypeByTypeId } from '../../../../common/application-types.tsx';
import type { ApplinksError } from '../../../../common/status/errors.tsx';
import messages from '../messages.tsx';

import AuthLevelMismatchDescription from './complex/AuthLevelMismatchDescription.tsx';

type ErrorStatusSectionMessageProps = {
	err: ApplinksError;
	context: ApplicationLinkV3Status;
};

type StatusAction = 'auth' | 'edit' | 'update';

type ErrorContents = {
	title: string;
	description: ReactNode;
	action?: StatusAction;
};

export const ErrorStatusSectionMessage = (props: ErrorStatusSectionMessageProps) => {
	const { formatMessage } = useIntl();

	const buildErrorContents = useCallback((): ErrorContents => {
		switch (props.err.type) {
			case 'CONNECTION_REFUSED':
				return {
					title: formatMessage(messages.connectionRefusedTitle),
					description: formatMessage(messages.connectionRefusedDescription, {
						rpcUrl: props.context.link.rpcUrl,
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
						code: (chunks: ReactNode[]) => <Code>{chunks}</Code>,
					}),
					action: 'edit',
				};
			case 'UNKNOWN_HOST':
				return {
					title: formatMessage(messages.unknownHostTitle),
					description: formatMessage(messages.unknownHostDescription, {
						rpcUrl: props.context.link.rpcUrl,
						code: (chunks: ReactNode[]) => <Code>{chunks}</Code>,
					}),
					action: 'edit',
				};
			case 'SSL_UNTRUSTED':
				return {
					title: formatMessage(messages.sslUntrustedTitle),
					description: formatMessage(messages.sslUntrustedDescription, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'SSL_HOSTNAME_UNMATCHED':
				return {
					title: formatMessage(messages.sslHostnameUnmatchedTitle),
					description: formatMessage(messages.sslHostnameUnmatchedDescription, {
						applicationName: props.context.link.name,
						rpcUrl: <Code>{props.context.link.rpcUrl}</Code>,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'SSL_UNMATCHED':
				return {
					title: formatMessage(messages.sslUnmatchedTitle),
					description: formatMessage(messages.sslUnmatchedDescription, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'OAUTH_PROBLEM':
				return {
					title: formatMessage(messages.oauthProblemTitle),
					description: formatMessage(messages.oauthProblemDescription, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'OAUTH_TIMESTAMP_REFUSED':
				return {
					title: formatMessage(messages.oauthTimestampRefusedTitle),
					description: formatMessage(messages.oauthTimestampRefusedDescription, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'OAUTH_SIGNATURE_INVALID':
				return {
					title: formatMessage(messages.oauthSignatureInvalidTitle),
					description: formatMessage(messages.oauthSignatureInvalidDescription, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'UNEXPECTED_RESPONSE':
				return {
					title: formatMessage(messages.unexpectedResponseTitle),
					description: formatMessage(messages.unexpectedResponseDescription, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'UNEXPECTED_RESPONSE_STATUS':
				return {
					title: formatMessage(messages.unexpectedResponseTitle),
					description: formatMessage(messages.unexpectedResponseDescription, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'CREATION_FAILED':
				return {
					title: formatMessage(messages.creationFailedTitle),
					description: formatMessage(messages.creationFailedDescription),
				};
			case 'CONFIGURATION_INCOMPLETE':
				return {
					title: formatMessage(messages.configurationIncompleteTitle),
					description: formatMessage(messages.configurationIncompleteDescription),
				};
			case 'IN_PROGRESS':
				return {
					title: formatMessage(messages.inProgressTitle),
					description: formatMessage(messages.inProgressDescription),
				};
			case 'SUBMISSION_FAILED':
				return {
					title: formatMessage(messages.submissionFailedTitle),
					description: formatMessage(messages.submissionFailedDescription),
				};
			case 'CONFIGURATION_COMPLETE':
				return {
					title: formatMessage(messages.configurationCompleteTitle),
					description: formatMessage(messages.configurationCompleteDescription),
				};
			case 'AUTH_LEVEL_MISMATCH': {
				return {
					title: formatMessage(messages.authLevelMismatchTitle),
					description: <AuthLevelMismatchDescription status={props.context} />,
					action: 'edit',
				};
			}
			case 'NO_REMOTE_APPLINK':
				return {
					title: formatMessage(messages.noRemoteApplinkTitle),
					description: formatMessage(messages.noRemoteApplinkDescription, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'INSUFFICIENT_REMOTE_PERMISSION':
			case 'REMOTE_VERSION_INCOMPATIBLE':
				return {
					title: formatMessage(messages.remoteVersionIncompatibleTitle),
					description: formatMessage(messages.remoteVersionIncompatibleDescription, {
						applicationName: props.context.link.name,
						// TODO: Try determine whether the link is DC or not (it doesn't strictly matter, as it'll still say "Confluence", etc)
						applicationType: lookupTypeByTypeId(props.context.link.type, false).name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'NO_OUTGOING_AUTH':
				return {
					title: formatMessage(messages.noOutgoingAuthTitle),
					description: formatMessage(messages.noOutgoingAuthDescription),
				};
			case 'GENERIC_LINK':
				return {
					title: formatMessage(messages.genericLinkTitle),
					description: formatMessage(messages.genericLinkDescription),
				};
			case 'NON_ATLASSIAN':
				return {
					title: formatMessage(messages.nonAtlassianTitle),
					description: formatMessage(messages.nonAtlassianDescription),
				};
			case 'SYSTEM_LINK':
				return {
					title: formatMessage(messages.systemLinkTitle),
					description: formatMessage(messages.systemLinkDescription),
				};
			case 'AUTH_LEVEL_UNSUPPORTED':
				return {
					title: formatMessage(messages.authLevelUnsupportedTitle),
					description: formatMessage(messages.authLevelUnsupportedDescription),
					action: 'edit',
				};
			case 'LEGACY_UPDATE':
				return {
					title: formatMessage(messages.legacyUpdateTitle),
					description: (
						<Stack space="space.100">
							<Text>{formatMessage(messages.legacyUpdateDescription1)}</Text>
							<Text>{formatMessage(messages.legacyUpdateDescription2)}</Text>
						</Stack>
					),
					action: 'update',
				};
			case 'LEGACY_REMOVAL':
				return {
					title: formatMessage(messages.legacyRemovalTitle),
					description: (
						<Stack space="space.100">
							<Text>{formatMessage(messages.legacyRemovalDescription1)}</Text>
							<Text>{formatMessage(messages.legacyRemovalDescription2)}</Text>
						</Stack>
					),
					action: 'update',
				};
			case 'MANUAL_LEGACY_UPDATE':
				return {
					title: formatMessage(messages.manualLegacyUpdateTitle),
					description: (
						<Stack space="space.100">
							<Text>{formatMessage(messages.manualLegacyUpdateDescription1)}</Text>
							<Text>{formatMessage(messages.manualLegacyUpdateDescription2)}</Text>
						</Stack>
					),
				};
			case 'MANUAL_LEGACY_REMOVAL':
				return {
					title: formatMessage(messages.manualLegacyRemovalTitle),
					description: (
						<Stack space="space.100">
							<Text>{formatMessage(messages.manualLegacyRemovalDescription1)}</Text>
							<Text>
								{formatMessage(messages.manualLegacyRemovalDescription2, {
									bold: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
								})}
							</Text>
							<Text>
								{formatMessage(messages.manualLegacyRemovalDescription3, {
									applicationName: props.context.link.name,
									link: (chunks: ReactNode[]) => (
										<Link href={props.context.link.displayUrl} target="_blank">
											{chunks}
										</Link>
									),
								})}
							</Text>
						</Stack>
					),
					action: 'update',
				};
			case 'UNKNOWN':
			default:
				return {
					title: formatMessage(messages.unknownTitle),
					description: formatMessage(messages.unknownDescription, {
						applicationName: props.context.link.name,
						rpcUrl: <Code>{props.context.link.rpcUrl}</Code>,
					}),
					action: 'edit',
				};
		}
	}, [props.err, props.context, formatMessage]);

	const buildActions = useCallback(() => {
		switch (buildErrorContents().action) {
			case 'edit': {
				// We don't show edit actions on the edit page
				// However when this class is extended to support popups on the list page, we'll show it there
				return [];
			}
			case 'update': {
				return [];
				// return [
				// 	<SectionMessageAction key="action-update" href="#">
				// 		{formatMessage(messages.actionUpdateNonfinal)}
				// 	</SectionMessageAction>,
				// ];
			}
			default:
				return [];
		}
	}, [buildErrorContents]);

	return (
		<SectionMessage appearance="error" title={buildErrorContents().title} actions={buildActions()}>
			<Stack space="space.100">
				<Text>{buildErrorContents().description}</Text>
				{props.context.error && props.context.error.details && (
					<CodeBlock text={props.context.error.details} />
				)}
			</Stack>
		</SectionMessage>
	);
};

export default ErrorStatusSectionMessage;
